//// Application Configuration
import local_configuration from '../../configurations/iat-ui-config.json'

//// Application Packages
import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

//// Application Modules

//// Application Slices
import { updateSettings, selectSettings } from '../../reducers/settingSlice'
import {
    createAuthorizations,
    deleteAuthorizations,
    selectAuthorizations,
} from '../../reducers/authorizationSlice'
import {
    createAccountAuxiliary,
    deleteAccountData,
    deleteAccountValidity,
    deleteAccountAuxiliary,
} from '../../reducers/accountSlice'
import {
    createFormAuxiliary,
    createSubFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    deleteFormValidity,
    deleteFormAuxiliary,
    updateSubFormAuxiliary,
    deleteSubFormData,
    deleteSubFormValidity,
    deleteSubFormAuxiliary,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import SideNavLinkComponent from './form-menu-navigation-side-nav-link-component'

//// Carbon Components
import {
    Header,
    HeaderContainer,
    HeaderMenuButton,
    HeaderName,
    HeaderGlobalBar,
    HeaderNavigation,
    HeaderMenuItem,
    SkipToContent,
    SideNav,
    SideNavItems,
    SideNavLink,
    Theme,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////////////// Component
const FormMenuNavigationComponent = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { pathname } = location

    //////// State

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const auth = useSelector(selectAuthorizations)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const project_name = settingsData?.['application']?.['project_name']
    const application_name = settingsData?.['application']?.['application_name']
    const cookie = auth?.['tokens']?.['cookie']
    const form = auth?.['tokens']?.['form']
    const side_nav_expanded = settingsData?.['interface']?.['side_nav_expanded']
    const view_links = settingsData?.['interface']?.['view_links']
    const config = settingsData?.['category']

    //////// Search Params

    //////// Methods

    //////// Functional Methods

    //////// Methods

    // Create Form Menu Array
    const generate = (config, pathname) => {
        const links = []
        const { required, dynamic } = config
        const formsNames = Object?.keys(config)

        // Create Items
        if (formsNames?.length > 0) {
            formsNames.forEach((el) => {
                // Create Side Navigation Link Object
                const item = {
                    ...config[el],
                }

                // Check If Link Is Category Link Or Form Link
                if (
                    item?.category_digital_name &&
                    item?.category_written_name &&
                    item?.category_route &&
                    item?.current_route &&
                    item?.category_route === item?.current_route
                ) {
                    item.link = item?.category_route
                } else if (
                    item?.category_digital_name &&
                    item?.category_written_name &&
                    item?.category_route &&
                    item?.digital_name &&
                    item?.written_name &&
                    item?.category_route !== item?.current_route
                ) {
                    item.link = item?.category_route + item?.current_route
                }

                // Check If Link Is Required
                if (item?.digital_name && required) {
                    const index = required.indexOf(item?.digital_name)
                    if (index > -1) {
                        item.required = true
                    } else {
                        item.required = false
                    }
                }

                // Check If Link Data Is Present
                if (item?.link === pathname) {
                    item.active = true
                } else {
                    item.active = false
                }

                // Check If Link Has Required Data
                if (
                    item?.category_digital_name &&
                    item?.digital_name &&
                    item?.written_name &&
                    item?.link &&
                    (item.required === true || item.required === false) &&
                    (item.active === true || item.active === false)
                ) {
                    // Create Link Item Object
                    const link = {
                        category_route: item?.category_route,
                        current_route: item?.current_route,
                        written_name: item?.written_name,
                        link: item?.link,
                        required: item.required,
                        active: item.active,
                    }

                    // Create Array Of Dynamic Form Configurations
                    const dynamicForms = Object?.keys(dynamic)

                    // Check For Dynamic Configurations
                    if (dynamicForms?.length > 0) {
                        // Find Index Of Dynamic Configuration
                        const dynamicIndex = dynamicForms?.indexOf(
                            item?.digital_name
                        )

                        // Check For Existing Index Of Dynamic Form Configuration
                        if (dynamicIndex > -1) {
                            // Extract Configutation Of Dynamic Form
                            const dynamicRules = dynamic[item?.digital_name]

                            // Check For Dynamic Rules From Dynamic Form Configuration
                            if (
                                dynamicRules?.['form-required'] &&
                                dynamicRules?.['question_required'] &&
                                dynamicRules?.['value_required']
                            ) {
                                // Extract Required Rules
                                const formRequired =
                                    dynamicRules?.['form-required']
                                const questionRequired =
                                    dynamicRules?.['question_required']
                                const valueRequired =
                                    dynamicRules?.['value_required']
                                const requiredValue =
                                    formsData?.[formRequired]?.values?.[
                                        questionRequired
                                    ]

                                // Check That Required Value Matches Value Required To Pass
                                if (requiredValue === valueRequired) {
                                    links.push(link)
                                }
                            } else {
                                links.push(link)
                            }
                        } else {
                            links.push(link)
                        }
                    } else {
                        links.push(link)
                    }
                }
            })
        }

        // Dispatch Links
        if (links?.length > 0) {
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        view_links: links,
                    },
                })
            )
            return view_links
        } else {
            return null
        }
    }

    //////// Functional Methods

    // Handle Side Nav Expanded
    const handleSideNavExpanded = async () => {
        dispatch(
            updateSettings({
                digital_name: 'interface',
                settings: {
                    view_expanded: !side_nav_expanded,
                    side_nav_expanded: !side_nav_expanded,
                },
            })
        )
    }

    // Handle Logout
    const handleLogout = () => {
        //// Perform Browser Effects

        localStorage.clear()
        sessionStorage.clear()

        //// End Perform Browser Effects

        // Clear Authorizations
        dispatch(deleteAuthorizations())

        //// Paginated Data
        if (local_configuration) {
            const config = Object?.keys(local_configuration)
            if (config?.length > 0) {
                //// Clear Account Data, Errors & Settings
                const account_config = local_configuration?.['pages']
                if (account_config) {
                    const titles = Object?.keys(account_config)
                    if (titles?.length > 0) {
                        titles?.forEach((el) => {
                            dispatch(
                                deleteAccountData({
                                    digital_name: el,
                                })
                            )
                            dispatch(
                                deleteAccountValidity({
                                    digital_name: el,
                                })
                            )
                            dispatch(
                                deleteAccountAuxiliary({
                                    digital_name: el,
                                })
                            )
                        })
                    }
                }

                //// Clear Form Data, Errors & Settings
                const forms_config = local_configuration?.['forms']
                if (forms_config) {
                    const categories = Object?.keys(forms_config)
                    if (categories?.length > 0) {
                        categories.forEach((el) => {
                            const forms = forms_config[el]
                            if (forms) {
                                const titles = Object?.keys(forms)
                                if (titles?.length > 0) {
                                    titles.forEach((el) => {
                                        const title = el
                                        const form = forms?.[el]
                                        if (
                                            form?.['view_structure'] === 'form'
                                        ) {
                                            dispatch(
                                                deleteFormData({
                                                    digital_name: title,
                                                })
                                            )
                                            dispatch(
                                                deleteFormValidity({
                                                    digital_name: title,
                                                })
                                            )
                                            dispatch(
                                                deleteFormAuxiliary({
                                                    digital_name: title,
                                                })
                                            )
                                            if (form?.['auxiliary']) {
                                                dispatch(
                                                    updateFormAuxiliary({
                                                        digital_name: title,
                                                        auxiliary:
                                                            form?.auxiliary,
                                                    })
                                                )
                                            }
                                        } else if (
                                            form?.['view_structure'] ===
                                            'sub-forms'
                                        ) {
                                            const subForms = form?.['sub_forms']
                                            if (subForms?.length > 0) {
                                                subForms.forEach((el) => {
                                                    const subFormTitle = el
                                                    dispatch(
                                                        deleteSubFormData({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    dispatch(
                                                        deleteSubFormValidity({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    dispatch(
                                                        deleteSubFormAuxiliary({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    if (form?.['auxiliary']) {
                                                        dispatch(
                                                            updateSubFormAuxiliary(
                                                                {
                                                                    digital_name:
                                                                        title,
                                                                    digital_sub_name:
                                                                        subFormTitle,
                                                                    auxiliary:
                                                                        form?.auxiliary,
                                                                }
                                                            )
                                                        )
                                                    }
                                                })
                                            }
                                        }
                                    })
                                }
                            }
                        })
                    }
                }
            }
        }

        //// End Clear Existing Data

        // Restore Deep Link
        dispatch(createAuthorizations({ form: form }))

        //// Perform Browser Effects

        console.clear()
        window.location.reload()

        //// End Perform Browser Effects

        // Dispatch Configuration Data To Page & Form Data
        if (local_configuration) {
            // Restore Pages Defaults
            const pages = local_configuration?.['pages']
            if (pages) {
                const pageTitles = Object?.keys(pages)
                if (pageTitles?.length > 0) {
                    pageTitles.forEach((el) => {
                        const title = el
                        const page = page?.[el]
                        if (page?.['auxiliary']) {
                            const auxiliary = page?.auxiliary
                            dispatch(
                                createAccountAuxiliary({
                                    digital_name: title,
                                    auxiliary: auxiliary,
                                })
                            )
                        }
                    })
                }
            }

            // Restore Forms Defaults
            const forms = local_configuration?.['forms']
            if (forms) {
                const formTitles = Object?.keys(forms)
                if (formTitles?.length > 0) {
                    formTitles.forEach((el) => {
                        const title = el
                        const form = forms?.[el]
                        if (form?.['auxiliary']) {
                            const auxiliary = form?.auxiliary
                            dispatch(
                                createFormAuxiliary({
                                    digital_name: title,
                                    auxiliary: auxiliary,
                                })
                            )
                        }
                        if (!form?.['values'] && !form?.['auxiliary']) {
                            const subFormTitles = Object?.keys(form)
                            if (subFormTitles?.length > 0) {
                                subFormTitles.forEach((el) => {
                                    const subTitle = el
                                    const subForm = form?.[el]
                                    if (subForm?.['auxiliary']) {
                                        const subFormAuxiliary =
                                            subForm?.auxiliary
                                        dispatch(
                                            createSubFormValidity({
                                                digital_name: title,
                                                digital_sub_name: subTitle,
                                                auxiliary: subFormAuxiliary,
                                            })
                                        )
                                    }
                                })
                            }
                        }
                    })
                }
            }
        }
    }

    // Handle Navigation
    const handleNavigation = (e, link) => {
        e.preventDefault()
        navigate(link ? link : '/')
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Enable Navigation Link Configuration & Active Link Switching
    useEffect(() => {
        if (config && pathname) {
            generate(config, pathname)
        }
        return () => {}
    }, [config, pathname])

    //////////////// Render & Return
    return (
        <>
            <div className="fldoh-form-nav container">
                <HeaderContainer
                    render={() => (
                        <>
                            <Theme theme="white">
                                <Header
                                    aria-label={
                                        project_name
                                            ? project_name
                                            : '' + ' ' + application_name
                                            ? application_name
                                            : ''
                                    }
                                    className={
                                        side_nav_expanded
                                            ? 'side-nav-expanded'
                                            : 'side-nav-not-expanded'
                                    }
                                >
                                    <SkipToContent />
                                    <HeaderMenuButton
                                        aria-label="Open menu"
                                        onClick={handleSideNavExpanded}
                                    />

                                    <HeaderName
                                        href={''}
                                        prefix={
                                            project_name ? project_name : ''
                                        }
                                        onClick={(e) =>
                                            handleNavigation(e, '/')
                                        }
                                    >
                                        {application_name
                                            ? application_name
                                            : ''}
                                    </HeaderName>

                                    <HeaderGlobalBar>
                                        <HeaderNavigation
                                            aria-label={
                                                project_name
                                                    ? project_name
                                                    : '' +
                                                      ' ' +
                                                      '[' +
                                                      application_name
                                                    ? application_name
                                                    : '' + ']'
                                            }
                                        >
                                            {cookie ? (
                                                <HeaderMenuItem
                                                    href={``}
                                                    onClick={handleLogout}
                                                    target="_self"
                                                    rel="noreferrer noopener"
                                                    aria-label="logout-link"
                                                    type="link"
                                                >
                                                    Logout
                                                </HeaderMenuItem>
                                            ) : (
                                                <HeaderMenuItem
                                                    href={``}
                                                    onClick={(e) =>
                                                        handleNavigation(
                                                            e,
                                                            `/account/login`
                                                        )
                                                    }
                                                    target="_self"
                                                    rel="noreferrer noopener"
                                                    aria-label="login-link"
                                                    type="link"
                                                >
                                                    Login
                                                </HeaderMenuItem>
                                            )}
                                            <HeaderMenuItem
                                                href={
                                                    'https://wa-oit-contactus-prd.azurewebsites.net/'
                                                }
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                aria-label="contact-us-link"
                                                type="link"
                                            >
                                                Contact Us
                                            </HeaderMenuItem>
                                        </HeaderNavigation>
                                    </HeaderGlobalBar>
                                    <SideNav
                                        expanded={side_nav_expanded}
                                        aria-label="Side navigation"
                                    >
                                        <SideNavItems>
                                            {view_links.map((el, i) => (
                                                <SideNavLinkComponent
                                                    key={i}
                                                    required={el.required}
                                                    active={el.active}
                                                    link={el.link}
                                                    written_name={
                                                        el.written_name
                                                    }
                                                />
                                            ))}
                                            <div className="side-nav-system-links">
                                                {cookie ? (
                                                    <SideNavLink
                                                        className={'system'}
                                                        href={``}
                                                        onClick={handleLogout}
                                                        target="_self"
                                                        rel="noreferrer noopener"
                                                        aria-label="logout-link"
                                                        type="link"
                                                    >
                                                        Logout
                                                    </SideNavLink>
                                                ) : (
                                                    <SideNavLink
                                                        className={'system'}
                                                        href={``}
                                                        onClick={(e) =>
                                                            handleNavigation(
                                                                e,
                                                                `/account/login`
                                                            )
                                                        }
                                                        target="_self"
                                                        rel="noreferrer noopener"
                                                        aria-label="login-link"
                                                        type="link"
                                                    >
                                                        Login
                                                    </SideNavLink>
                                                )}
                                                <SideNavLink
                                                    className={'system'}
                                                    href={
                                                        'https://wa-oit-contactus-prd.azurewebsites.net/'
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    aria-label="contact-us-link"
                                                    type="link"
                                                >
                                                    Contact Us
                                                </SideNavLink>
                                            </div>
                                        </SideNavItems>
                                    </SideNav>
                                </Header>
                            </Theme>
                        </>
                    )}
                />
            </div>
        </>
    )
}

export default FormMenuNavigationComponent
